import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons/faArrowDownToLine";
import { faFile } from "@fortawesome/pro-regular-svg-icons/faFile";
import { faFilePdf } from "@fortawesome/pro-regular-svg-icons/faFilePdf";
import { faFileWord } from "@fortawesome/pro-regular-svg-icons/faFileWord";
import { faFileExcel } from "@fortawesome/pro-regular-svg-icons/faFileExcel";
import { faFileZipper } from "@fortawesome/pro-regular-svg-icons/faFileZipper";
import { faFileImage } from "@fortawesome/pro-regular-svg-icons/faFileImage";
import { faFileVideo } from "@fortawesome/pro-regular-svg-icons/faFileVideo";
import { faFileSlash } from "@fortawesome/pro-regular-svg-icons/faFileSlash";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useResultFile } from "./useFormResultUpload";

interface Props {
  element: DocumentElementType;
}

function iconForName(name: string): IconDefinition {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return faFilePdf;
  if (["doc", "docx"].includes(ext)) return faFileWord;
  if (["xls", "xlsx", "csv"].includes(ext)) return faFileExcel;
  if (["zip", "gz", "tar", "rar", "7z"].includes(ext)) return faFileZipper;
  if (["png", "jpg", "jpeg", "gif", "webp", "heic"].includes(ext))
    return faFileImage;
  if (["mp4", "mov", "webm", "avi", "mkv"].includes(ext)) return faFileVideo;
  return faFile;
}

/**
 * Read-only render of a page-document file block — a downloadable file card
 * (icon · name · size). The editor's rich version (with the file picker) lives
 * in apps/web; this is the shared renderer the read-only hosts (document portal
 * / PDF) use. Resolves the attachment through the host file transport.
 */
const PageFileView: React.FC<Props> = ({ element }) => {
  const data = (element.data ?? {}) as { attachmentId?: number | null };
  const attachmentId =
    typeof data.attachmentId === "number" ? data.attachmentId : null;
  const { file, missing } = useResultFile(attachmentId, { download: true });

  if (attachmentId == null) return null;

  // The file is gone or withheld from this reader: a card that says so, with
  // nothing to download, instead of one that waits.
  if (missing) {
    return (
      <div className="flex items-center gap-[11px] rounded-[10px] border border-[#e7e5e1] bg-white px-[14px] py-[11px]">
        <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] bg-[#f3f1ee] text-[#8f8b83]">
          <FontAwesomeIcon icon={faFileSlash} className="text-[15px]" />
        </span>
        <span className="min-w-0 flex-1 truncate font-jakarta text-[13.5px] font-semibold text-[#8f8b83]">
          File unavailable
        </span>
      </div>
    );
  }

  return (
    <a
      href={file?.url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-[11px] rounded-[10px] border border-[#e7e5e1] bg-white px-[14px] py-[11px]"
    >
      <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] bg-[#f3f1ee] text-[#8f8b83]">
        <FontAwesomeIcon
          icon={file ? iconForName(file.name ?? "") : faSpinner}
          className={file ? "text-[15px]" : "animate-spin text-[14px]"}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-jakarta text-[13.5px] font-semibold text-[#33302b]">
          {file?.name ?? "File"}
        </span>
        {file?.sizeLabel && (
          <span className="block font-jakarta text-[11.5px] font-medium text-[#a5a199]">
            {file.sizeLabel}
          </span>
        )}
      </span>
      <FontAwesomeIcon
        icon={faArrowDownToLine}
        className="flex-none text-[14px] text-[#b4b0a8]"
      />
    </a>
  );
};

export default PageFileView;
